import { useMsal } from "@azure/msal-react";
import { loginRequest, envConfig } from "../config/authConfig";
import { clearUserProfileCache, getCachedPhotoUrl, setCachedPhotoUrl } from "./useUserProfile";
import { useState, useEffect } from "react";

interface UserInfo {
  displayName?: string;
  name?: string;
  email?: string;
  photoUrl?: string;
}

export const useAuth = () => {
  const { instance, accounts } = useMsal();
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(() => getCachedPhotoUrl() || undefined);
  
  const isAuthenticated = accounts.length > 0;
  const account = accounts[0];

  // Fetch profile photo from Microsoft Graph (only if not already cached)
  useEffect(() => {
    const fetchProfilePhoto = async () => {
      if (isAuthenticated && account) {
        // Check cache first
        const cached = getCachedPhotoUrl();
        if (cached) {
          setPhotoUrl(cached);
          return;
        }

        try {
          const response = await instance.acquireTokenSilent({
            ...loginRequest,
            account: account,
          });

          const graphResponse = await fetch(`${envConfig.graphApiUrl}/me/photo/$value`, {
            headers: {
              Authorization: `Bearer ${response.accessToken}`,
            },
          });

          if (graphResponse.ok) {
            const blob = await graphResponse.blob();
            const url = URL.createObjectURL(blob);
            // Store in shared cache
            setCachedPhotoUrl(url);
            setPhotoUrl(url);
          }
        } catch (error) {
          console.log('Error fetching profile photo:', error);
          // Photo might not be available, that's okay
        }
      } else {
        setPhotoUrl(undefined);
      }
    };

    fetchProfilePhoto();
  }, [isAuthenticated, account, instance]);

  const userInfo: UserInfo | null = account
    ? {
        displayName: account.name,
        name: account.name,
        email: account.username,
        photoUrl: photoUrl,
      }
    : null;

  const login = async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const logout = () => {
    // Clear the user profile cache before signing out
    clearUserProfileCache();
    instance.logoutRedirect();
  };

  return {
    isAuthenticated,
    userInfo,
    login,
    logout
  };
};
